import React from "react";
import { Box, Button, Typography } from "@mui/material";
import PaymentMethodSetup from "components/payment/PaymentMethodSetup/PaymentMethodSetup";
import { removeSetupReturnParams } from "components/profile/paymentMethodSetupReturn";
import type { Request } from "types";
import type { ConfirmReservationFormState } from "../form/schemaValidation";
import type { QuotePaymentSetupAdapter } from "../useQuotePaymentSetup";
import LinkPaymentMethod from "components/globals/LinkPaymentMethod";
import { isLinkPaymentMethod } from "globals/utils/paymentMethods";

export const QuotePaymentForm = ({
  checkout,
  request,
  getValues,
  isSubmitting,
}: {
  checkout: QuotePaymentSetupAdapter;
  request: Request;
  getValues: () => ConfirmReservationFormState;
  isSubmitting?: boolean;
}) => {
  if (checkout.addCardReturn)
    return (
      <Typography>
        Complete Add Card, then select Reserve to continue.
      </Typography>
    );
  if (checkout.result)
    return (
      <Box>
        <Typography>
          {isLinkPaymentMethod(checkout.result) ? (
            <LinkPaymentMethod />
          ) : (
            `Card ending in ${checkout.result.card.last4}`
          )}{" "}
          is ready. Select Reserve to continue.
        </Typography>
        <Button disabled={isSubmitting} onClick={checkout.restart}>
          Use a different card
        </Button>
      </Box>
    );
  return (
    <Box mb={4}>
      <PaymentMethodSetup
        key={checkout.restartCount}
        ref={checkout.elementRef}
        operatorId={checkout.source.operatorId}
        currency={checkout.currency}
        savedPaymentMethods={checkout.savedPaymentMethods}
        canSaveForFutureBookings={checkout.savedPaymentMethods !== undefined}
        attemptScope={checkout.source.ownerKey}
        attemptGeneration={checkout.restartCount}
        context={{ surface: "quote", requestId: request.id }}
        billingDetails={{
          name: [
            request.bookingContact.firstName,
            request.bookingContact.lastName,
          ]
            .filter(Boolean)
            .join(" "),
          email: request.bookingContact.email,
          phone: request.bookingContact.mobilePhoneInternational,
        }}
        returnUrl={`${window.location.pathname}${removeSetupReturnParams(window.location.search)}`}
        initialAttempt={checkout.attempt}
        onAttemptCreated={checkout.onAttemptCreated}
        resume={checkout.resume}
        onBeforeConfirm={(id) => checkout.retain(id, getValues())}
        onStateChange={checkout.onStateChange}
      />
      {checkout.resume && !checkout.setupState?.error && (
        <Typography>
          Your card verification has returned. Select Reserve to check and
          continue.
        </Typography>
      )}
      {checkout.setupState?.restartRequired && (
        <Button disabled={isSubmitting} onClick={checkout.restart}>
          Enter card again
        </Button>
      )}
    </Box>
  );
};
